<template>
  <div class="ab__registration">
    <h2>{{ $t('ab.title') }} ({{ $t('ab.pageEdit') }})</h2>
    <div class="ab-page__set">
      <p class="title">
        <span class="label">
          <i>B</i>
        </span>
        {{ $t('ab.textResult.editContents') }}
        <template>
          <span class="count">
            <i v-if="model.data.length >= maxDataLength" class="icon icon--warning-red-triangle">
              <ui-tooltip arrowBottom arrowCenter>
                <span v-html="$t('ab.textResult.countWarning')"></span>
              </ui-tooltip>
            </i>
            <em>
              <span :class="{max: model.data.length >= maxDataLength, exist: model.data.length > 0}">{{model.data.length}}</span>
              <span :class="{max: model.data.length >= maxDataLength}">/{{maxDataLength}}</span>
              ({{ $t('ab.editLimit') }})
            </em>
          </span>
        </template>
        <span class="title-button">
          <ui-button :iconName="'icon--pencil-red'"
                     :hoverIconName="'icon--pencil-white'"
                     primary2
                     @click="onEdit">
            {{ $t('ab.edits') }}
          </ui-button>
        </span>
      </p>
      <template v-if="!loading">
        <div class="list-wrap"
             :style="{ marginRight: `-${scrollbarWidth}px` }">
          <div class="thead">
            <table :style="{ width: `calc(100% - ${scrollbarWidth}px)` }">
              <colgroup>
                <col width="5%">
                <col width="45%">
                <col width="45%">
                <col width="5%">
              </colgroup>
              <thead>
              <tr class="head">
                <th class="rank">&nbsp;</th>
                <th>
                  <sort-options
                  columnName="orgText"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                    @onSortOptionChange="onSortOptionChange">
                  {{ $t('ab.textResult.origin') }}
                  </sort-options>
                </th>
                <th>
                  <sort-options
                  columnName="text"
                    :sortColumn="sortColumn"
                    :isSortAsc="isSortAsc"
                    @onSortOptionChange="onSortOptionChange">
                  {{ $t('ab.textResult.editContents') }}
                  </sort-options>
                </th>
                <th class="rank">&nbsp;</th>
              </tr>
              </thead>
              <tbody>
                <tr v-for="(node, i) in sortedList" :key="`node-${node.textHtml}_${i}`">
                  <td class="rank">
                    <div class="box">
                    {{ i + 1 }}
                    </div>
                  </td>
                  <td>
                    <div class="box">
                    {{ node.orgText }}
                    </div>
                  </td>
                  <td>
                    <div class="box">
                    {{ node.text }}
                    </div>
                  </td>
                  <td class="rank">
                    <div class="box">
                      <button @click="removeData(node)">
                        <i class="icon icon--delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="model.data.length < 1"
             class="no-list">
            <div class="no-list__contents">
              <h3 v-html="$t('ab.textResult.empty.title')"></h3>
              <p v-html="$t('ab.textResult.empty.desc')"></p>
              <a href="https://forum.beusable.net/ko/post/871" target="_blank">
                <i class="icon icon--page-right-size14"></i>
                <span>{{ $t('ab.textResult.empty.link') }}</span>
              </a>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="button--wrap">
      <ui-button class="btn__cancel"
                 size="large"
                 style="opacity: 0;">
        {{ $t('common.prev') }}
      </ui-button>

      <ui-button class="btn__cancel"
                 size="large"
                 @click="onAddOrEditCancel">
        {{ $t('common.prev') }}
      </ui-button>

      <ui-button v-if="isEditMode"
                 class="btn__register"
                 size="large"
                 primary
                 @click="onModify">
        {{ $t('common.edit') }}
      </ui-button>
      <template v-else>
        <ui-button class="btn__register"
                   primary
                   size="large"
                   :disabled="model.data.length < 1"
                   :class="{disabled: model.data.length < 1}"
                   @click="onRegistration">
          {{ isEditable ?  $t('common.editCompleted') :  $t('common.register') }}
        </ui-button>
      </template>
    </div>
    <div class="popup" v-if="showCautionPopup">
      <a-b-caution-popup @closePopup="showCautionPopup = false" @confirm="onConfirm" />
    </div>
    <!-- 분석 시간 없을 경우 팝업   -->
    <AnalysisTimeSetting v-if="showAnalysisTimePopup" @closeTimePopup="showAnalysisTimePopup = $event">
    </AnalysisTimeSetting>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
// import { addPageText, modifyPageText } from '@/api/abtest'
import { getDialog } from '@/libs/vue-utils'
import UiButton from '@/components/ui/UiButton'
import SortOptions from '@/components/ui/SortOptions'
import ABCautionPopup from '@/components/report-ab/ABCautionPopup'
import 'vue-range-slider/dist/vue-range-slider.scss'
import { cloneDeep, utcToLocalTimezone } from '../../../../libs/utils'
import {
  addABTarget,
  addText,
  deleteText,
  getTextList,
  modifyABTarget,
  modifyPage,
  modifyText
} from '../../../../api/abtest'
import UiTooltip from '@/components/ui/UiTooltip'
import { isBefore, isSameDay, parse, startOfDay } from 'date-fns'
import AnalysisTimeSetting from '@/components/dashboard/AnalysisTimeSetting.vue'
import store from '@/store'

const openDialog = getDialog('customData')

export default {
  name: 'ABAddOrEditResult',
  components: {
    AnalysisTimeSetting,
    SortOptions,
    UiButton,
    ABCautionPopup,
    UiTooltip
  },
  props: [
    'traffic',
    'pageName',
    'timezone',
    'timezoneName',
    'startDate',
    'endDate',
    'targetList'
  ],
  data () {
    return {
      loading: true,
      editing: false,
      model: {
        data: []
      },
      orgRequestData: [],
      requestData: [],
      originalData: [],
      abAid: null,
      scrollbarWidth: 0,
      maxDataLength: 20,
      sortColumn: 'updateDate',
      isSortAsc: false,
      showCautionPopup: false,
      registerEnabled: false,
      textWindow: false,
      startDateAndTime: null,
      endDateAndTime: null,
      showAnalysisTimePopup: false
    }
  },
  created () {
    if (!this.pageName) {
      this.$router.go(-1)
    }
    this.model.data = []

    let scrollDiv = document.createElement('div')
    scrollDiv.style.cssText = 'width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;'
    document.body.appendChild(scrollDiv)

    let scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth
    this.scrollbarWidth = scrollbarWidth

    document.body.removeChild(scrollDiv)

    if (this.isEditMode) {
      this.abAid = this.$route.params.abAid
      // this.model.data = // TODO: 수정 모드에서 초기 데이터 넣기
      // this.originalData = // TODO: 수정 모드에서 초기 데이터 넣기

      // TODO: 데이터 조회 API
      this.loading = false
    } else {
      this.loading = false
    }

    window.addEventListener('message', this.handleMessage, false)
  },
  destroyed () {
    window.removeEventListener('message', this.handleMessage)
  },
  computed: {
    ...mapState([
      'abEditValues',
      'userData',
      'applyData',
      'abList'
    ]),
    sortedList () {
      const list = cloneDeep(this.model.data)
      return list.sort((a, b) => {
        const modifier = this.isSortAsc ? 1 : -1
        if (a[this.sortColumn] < b[this.sortColumn]) return -1 * modifier
        if (a[this.sortColumn] > b[this.sortColumn]) return 1 * modifier
        return 0
      })
    },
    aid () {
      return this.$route.params.aid
    },
    isEditMode () {
      return this.$route.name === 'ab.edit.text'
    },
    isEditable () {
      return this.$route.query.editable
    },
    isEditExist () {
      return JSON.stringify(this.originalData) !== JSON.stringify(this.model.data)
    }
  },
  watch: {
    'model.traffic' (value) {
      if (!this.isEditMode && value > this.availableTraffic) {
        this.model.traffic = this.availableTraffic
      }
    },
    isEditable: {
      immediate: true,
      handler (val) {
        if (val && this.abEditValues) {
          this.getTextList()
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'storeABList'
    ]),
    getTextList () {
      getTextList({
        orgAid: this.abEditValues.orgAid,
        aid: this.abEditValues.abAid
      }).then((result) => {
        result.forEach((v) => {
          v.openHtml = false
        })
        this.orgRequestData = result
        this.model.data = result.map((item) => {
          return {
            ...item,
            isSavedData: true
          }
        })
      })
    },
    handleMessage (result) {
      if (result.data === 'ab_edit_start') {
        window.abTestTextWindow.postMessage(this.model.data, '*')
      } else {
        if (result.data._ab_text) {
          this.onEditFinish(result.data._ab_text.data)
        }
      }
    },
    onEdit () {
      this.editing = true
      this.registerEnabled = false
      const isExistUrlParameter = this.applyData.url.indexOf('?') > -1
      console.log('here')
      window.abTestTextWindow = window.open(this.applyData.url + (isExistUrlParameter ? '&' : '?') + '_beusable_ab_edit' + `&_beusable_lang=${this.$i18n.locale}`)
    },
    onEditFinish (data) {
      this.editing = false

      this.model.data = data
    },
    removeData (selector) {
      if (selector._id) {
        deleteText({ id: selector._id })
          .then(() => {

          })
          .catch(console.error)
      }

      this.model.data.splice(this.model.data.map(el => el.textHtml).indexOf(selector.textHtml), 1)
      this.registerEnabled = false
    },
    onExcel () {
      // TODO: 엑셀 다운로드
      console.log('TODO: 엑셀 다운로드')
    },
    onPreview () {
      // const values = {
      //   aid: this.applyData._id,
      //   data: this.model.data
      // }

      // TODO: 미리보기
      console.log('TODO: 미리보기')
      this.registerEnabled = true
      // this.loading = true
      // addPageText(values)
      //   .then(() => {
      //     this.onAddorEditDone()
      //     this.loading = false
      //   })
      //   .catch(err => {
      //     const errorMessage = this.getValicationErrorText(err.message)
      //     openDialog({
      //       type: 'alert',
      //       iconClass: 'icon--dialog-warning',
      //       title: this.$t('dialog.common.title'),
      //       description: errorMessage
      //     })
      //     this.loading = false
      //   })
    },
    onRegistration () {
      // const values = {
      //   aid: this.applyData._id,
      //   data: this.model.data
      // }

      this.loading = false
      this.showCautionPopup = true
    },
    async onEditWriting () {
      try {
        const { orgStartDate, orgEndDate, targetList, ...cleaned } = this.abEditValues
        await modifyPage(cleaned)

        const orgTargetList = this.targetList.map(item => ({
          id: item.id,
          aid: item.aid,
          userId: this.userData.userId,
          userNum: this.userData.userNum,
          regDate: item.regDate,
          sid: this.applyData.sid,
          deleted: item.deleted
        }))

        if (orgTargetList.length > 0) {
          await modifyABTarget({
            abid: this.abEditValues.abAid,
            data: orgTargetList
          })
        }
      } catch (err) {
        const errorMessage = this.getValicationErrorText(err.message)
        if (err.message === 'AB_ALREADY_STARTED') {
          this.AB_ALREADY_STARTED = true
          this.showAnalysisTimePopup = true
        } else {
          openDialog({
            type: 'alert',
            iconClass: 'icon--dialog-warning',
            title: this.$t('dialog.common.title'),
            description: errorMessage
          })
        }
      }
    },
    onAddorEditDone () {
      this.storeABList(this.aid).then(() => {
        this.$router.push({ name: 'ab.list', params: this.$route.params })
      }).catch(console.error)
    },
    onAddOrEditList () {
      if (this.isEditExist) {
        openDialog({
          type: 'confirm',
          iconClass: 'icon--dialog-warning',
          title: this.$t('ab.exitCheck.title'),
          description: this.$t('ab.exitCheck.desc')
        }).then(result => {
          if (result) {
            this.$router.push({ name: 'ab.list', params: { aid: this.$route.params.aid } })
          }
        })
      }
    },
    onAddOrEditCancel () {
      if (this.isEditable) {
        return this.$router.push({
          name: 'ab.edit.text',
          params: {
            aid: this.$route.params.aid,
            abAid: this.abEditValues.abAid
          }
        })
      }

      if (this.isEditExist) {
        openDialog({
          type: 'confirm',
          iconClass: 'icon--dialog-warning',
          title: this.$t('ab.exitCheck.title'),
          description: this.$t('ab.exitCheck.desc')
        }).then(result => {
          if (result) {
            this.model.data = []
            this.$router.push({ name: 'ab.add.text', params: { aid: this.$route.params.aid, traffic: this.traffic, pageName: this.pageName } })
          }
        })
      } else {
        this.model.data = []
        this.$router.push({ name: 'ab.add.text', params: { aid: this.$route.params.aid, traffic: this.traffic, pageName: this.pageName } })
      }
    },
    getChangeItems (newValue, oldValue) {
      const changed = []

      for (const processed of newValue) {
        const original = oldValue.find(o => o.textHtml === processed.textHtml)

        if (!original) {
          changed.push(processed)
          continue
        }

        if (original.text !== processed.text) {
          changed.push(processed)
        }
      }

      return changed
    },
    onSortOptionChange (column, isAsc) {
      if (this.sortColumn === column && this.isSortAsc === isAsc) {
        return
      }
      this.sortColumn = column
      this.isSortAsc = isAsc
    },
    async onConfirm () {
      let startDate = this.$route.params.startDate
      if (this.$route.params.onTimes === 'true') {
        this.startDateAndTime = this.$route.params.startDate
        this.endDateAndTime = this.$route.params.endDate
        startDate = this.startDateAndTime
      }

      const now = utcToLocalTimezone(new Date(), this.applyData.timezoneName)
      const startHour = this.startDateAndTime && parse(this.startDateAndTime).getHours()
      const startMinute = this.startDateAndTime && parse(this.startDateAndTime).getMinutes()
      const endHour = this.endDateAndTime && parse(this.endDateAndTime).getHours()
      if (this.startDateAndTime && isSameDay(startDate, now) && (startHour < now.getHours() ||
        (isSameDay(startDate, now) && startHour === now.getHours() && startMinute < now.getMinutes())) ||
        isBefore(startOfDay(startDate), startOfDay(now)) ||
        endHour < startHour) {
        this.showAnalysisTimePopup = true
        this.showCautionPopup = false
        return
      }

      const requestData = this.model.data

      try {
        let inserted
        const payload = {
          orgAid: this.$route.params.aid,
          data: requestData,
          traffic: this.traffic,
          pageName: this.pageName,
          timezone: this.timezone,
          timezoneName: this.timezoneName,
          startDate: this.startDateAndTime || this.$route.params.startDate,
          endDate: this.endDateAndTime || this.$route.params.endDate
        }

        if (requestData.length > 0) {
          if (this.isEditable) {
            // 수정시
            inserted = await modifyText({
              ...payload,
              aid: this.abEditValues.abAid
            })
          } else {
            // 등록시
            inserted = await addText(payload)
          }
        }

        const targetList = this.targetList.map(item => ({
          aid: item.aid,
          userId: this.userData.userId,
          userNum: this.userData.userNum,
          regDate: item.regDate,
          sid: this.applyData.sid,
          deleted: false
        }))

        if (this.isEditable) {
          this.showCautionPopup = false
          await this.onEditWriting()
        } else {
          await addABTarget({
            abid: inserted.bPage._id,
            data: targetList
          })
        }

        await this.storeABList(this.aid)
        this.model.data = []
        store.commit('clearAbEditValues')
        this.$router.push({ name: 'ab.list', params: this.$route.params })
      } catch (err) {
        console.error(err)
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    const stayPages = [
      'ab.edit.text',
      'ab.add.text.result'
    ]

    const isStayingWithinGroup =
      stayPages.includes(to.name) && stayPages.includes(from.name)

    if (!isStayingWithinGroup) {
      store.commit('clearAbEditValues')
    }

    if (this.isEditExist) {
      openDialog({
        type: 'confirm',
        iconClass: 'icon--dialog-warning',
        title: this.$t('ab.exitCheck.title'),
        description: this.$t('ab.exitCheck.desc')
      }).then(result => {
        if (result) {
          this.model.data = []
          next()
        }
      })
    } else {
      this.model.data = []
      next()
    }
  }
}
</script>

<style lang="scss" scoped>

.ab__registration {
  box-sizing: border-box;
  padding: 30px 30px 10px 30px;
  overflow-y: auto;

  h2 {
    font-size: 22px;
    font-weight: 500;
    color: #444444;
  }

  .ab-page__set {
    margin-top: 30px;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #ffffff;
    box-sizing: border-box;
    position: relative;

    p.title {
      display: flex;
      align-items: center;
      column-gap: 10px;
      padding: 25px;
      font-size: 15px;
      font-weight: 600;
      color: #2f2f2f;
      line-height: 21px;
      position: relative;

      .label {
        display: inline-block;
        position: relative;
        width: 38px;
        height: 38px;
        border-radius: 5px;
        background-color: #1199af;

        i {
          @include positionXYcenter;
          font-size: 20px;
          font-weight: 500;
          color: #ffffff;
        }
      }

      .count {
        display: flex;
        column-gap: 5px;
        align-items: center;
        vertical-align: middle;
        font-size: 13px;
        color: #444;
        line-height: 15px;

        em {
          font-size: 12px;
          color: #888;

          span.exist {
            color: #1199af;
          }

          span.max {
            color: #e62020;
          }
        }

        .icon {
          .bubble {
            display: none;
            left: 49px;
            top: -66px;
          }
          &:hover .bubble {
            display: block;
          }
        }
      }

      .title-button {
        position: absolute;
        top: 50%;
        right: 25px;
        transform: translateY(-50%);

        .ui-btn {
          margin-left: 5px;
        }
      }
    }

    .no-list {
      display: flex;
      justify-content: center;
      align-items: center;

      width: 100%;
      padding: 65px 0px 130px 0px;

      &__contents {
        width: 620px;
        display: flex;
        flex-direction: column;
        align-items: center;

        box-sizing: border-box;

        h3 {
          font-size: 20px;
          font-weight: 500;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.25;
          letter-spacing: normal;
          text-align: center;
          color: #444;
        }

        p {
          margin-top: 12px;
          padding: 0px 30px;

          font-size: 14px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.43;
          letter-spacing: normal;
          text-align: center;
          color: #2f2f2f;
        }

        a {
          display: flex;
          column-gap: 5px;
          align-items: center;

          margin-top: 22px;

          span {
            font-size: 12px;
            font-weight: 500;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: #1199af;
          }
        }
      }
    }

    .list-wrap {
      position: relative;
      padding: 0 25px 28px;

      .tbody {
        height: 100vh;
        max-height: calc(100vh - 393px);
        overflow-y: scroll;
        overflow-x: hidden;

        &::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 25px;
          width: calc(100% - 70px);
          height: 17px;
          background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff);
        }

        table {
          margin-bottom: 10px;
        }
      }

      table {
        width: 100%;

        tr.head {
          background: #555;
          background: linear-gradient(to right, #555, #1199af);
          border-radius: 5px;
        }

        th {
          text-align: center;
          padding: 0 10px;
          height: 50px;
          background-color: #1199af;
          font-size: 13px;
          font-weight: normal;
          line-height: 1.23;
          color: #fff;
          white-space: nowrap;

          &:first-child {
            transform: translateX(1px);
            border-radius: 5px 0 0 5px;
            padding-left: 15px;
          }

          &:nth-child(1),
          &:nth-child(2) {
            background-color: #555;
          }

          &:last-child {
            transform: translateX(-1px);
            border-radius: 0 5px 5px 0;
            padding-right: 15px;
          }

          &.rank {
            text-align: center;
          }
        }

        td {
          padding: 16px 15px;
          border-bottom: 1px solid #ebebeb;
          font-size: 14px;
          line-height: 1.36;
          color: #1199af;
          word-break: break-all;
          text-align: center;

          &:nth-child(1),
          &:nth-child(2) {
            color: #2f2f2f;
          }

          .box {
            overflow: hidden;
          }

          &.rank {
            text-align: center;
          }
        }
      }
    }
  }

  .button--wrap {
    margin: 30px 0px;
    text-align: center;

    button {
      &.btn__list {
        float: left;
      }

      &.btn__register {
        float: right;
        margin-left: 5px;
      }
    }
  }

  .btn__confirm {
    background-color: $brandColor;
    border: 0 !important;
    margin-left: 5px;

    &:hover {
      background-color: $brandColor !important;
    }

    .btn-text {
      color: #fff;
    }
  }
}
.popup {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  background: rgba(0, 0, 0, 0.6);
  z-index: 999;
}
</style>
